import React, { useEffect, useMemo, useState, useCallback } from "react";
import { DateTime } from "luxon";
import { cents, modules } from "byzantine";
import filters from "byzantine/src/filters";
import {
  Table,
  Checkbox,
  TableDateInput,
  TableInput,
  Chip,
  IconName,
  TableSelect,
  Toggle,
} from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { InstitutionFeatures } from "byzantine/src/types";
import { MAX_MEMO_LENGTH } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import {
  BulkPaymentFormType,
  BulkPaymentRowType,
} from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import * as formAdapters from "../../../../src/adapters";
import { useUserFeatures } from "../../../contexts/UserFeaturesContext";
import { getAchImportPermittedActions } from "../achImportDefaults";
import styles from "./BulkPaymentTable.module.scss";
import {
  SelectedRecipientCard,
  RecipientSelector,
  getRecipientError,
  getAmountError,
  getDateError,
  getTransactionTypeError,
} from "./";

type BulkPaymentRowProps = {
  payment: BulkPaymentRowType;
  index: number;
  rowNumber?: number;
  form: any;
  recipients: API.Recipient[];
  selectedRows: number[];
  toggleRowSelection: (index: number) => void;
  handleClickEditRecipient: (
    recipientId: API.RecipientId,
    rowIndex?: number
  ) => void;
  handleClickAddRecipient: (rowIndex: number) => void;
  handleClickUnsavedRecipient?: (
    recipient: API.Recipient,
    rowIndex: number
  ) => void;
  minDate: string;
  setCustomAlertCopy: (copy: string) => void;
  eligibilityCache: Record<string, boolean>;
  isTemplateMode?: boolean;
  isExcluded?: boolean;
  onToggleInclude?: () => void;
  isFieldEditable?: (
    field: "amount" | "description" | "recipient" | "transactionType" | "date"
  ) => boolean;
  allowExpedite?: boolean;
  isImportMode?: boolean;
};

export const SPEED_COPY_CONFIG = {
  instant: {
    available: "Instant available",
    sending: "Sending instantly",
    icon: "zap" as IconName,
    // user will never be able to collect instantly, but keeping for consistency
    collecting: "Collecting instantly",
  },
  sameDay: {
    available: "Same-day available",
    sending: "Sending same day",
    icon: "clock" as IconName,
    collecting: "Collecting same day",
  },
  standard: {
    // we never display standard as available, but keeping for consistency
    available: "Standard available",
    sending: "Sending standard",
    icon: "calendar" as IconName,
    collecting: "Collecting standard",
  },
};

const getSpeedWithExpeditedOption = (
  speedStatus: NonNullable<BulkPaymentRowType["speedStatus"]>,
  expediteOption: BulkPaymentFormType["chosenExpediteOption"]
): NonNullable<BulkPaymentRowType["speedStatusWithExpediteOption"]> => {
  if (!speedStatus || !expediteOption) {
    return "";
  }

  if (expediteOption === "fastest" || expediteOption === "fedNow") {
    return speedStatus;
  } else if (expediteOption === "sameDay") {
    if (speedStatus === "instant" || speedStatus === "sameDay") {
      return "sameDay";
    }
    return speedStatus;
  }

  return "";
};

const useSpeedStatus = (
  features: InstitutionFeatures,
  payment: BulkPaymentRowType,
  eligibilityCache: Record<string, boolean>
) => {
  const { checkReceiveInstantEligibility, loading } =
    modules.transfers.useCheckReceiveInstantEligibility();

  const [speedStatus, setSpeedStatus] = useState<
    NonNullable<BulkPaymentRowType["speedStatus"]>
  >(payment.speedStatus || "");

  const {
    fednow_send: fedNowFeature,
    same_day_ach: sameDayFeature,
    expedited_payments: expeditedPaymentsFeature,
  } = features;

  const hasRecipient = Boolean(payment.recipient?.id);
  const routingNumber = payment.recipient?.ach_destination?.routing_number;
  const { scheduledDate, transactionType } = payment;

  useEffect(() => {
    if (!hasRecipient || !routingNumber || !expeditedPaymentsFeature) {
      setSpeedStatus("");
      return;
    }

    const sameDayOptionOnly = sameDayFeature && !fedNowFeature;
    const fedNowOptionOnly = fedNowFeature && !sameDayFeature;
    const sameDayAndFedNowOptionsOnly = sameDayFeature && fedNowFeature;

    const date = DateTime.fromFormat(scheduledDate, "MM/dd/yyyy");
    const today = DateTime.now().startOf("day");
    const tomorrow = today.plus({ days: 1 });
    const todayPayment = date.hasSame(today, "day");
    const tomorrowPayment = date.hasSame(tomorrow, "day");

    const isCollectPayment = transactionType === "collect";
    const isSendPayment = transactionType === "send";

    const determineStatus = async () => {
      let status: BulkPaymentRowType["speedStatus"] = "standard";

      const fetchEligibility = async () => {
        if (eligibilityCache[routingNumber] !== undefined) {
          return eligibilityCache[routingNumber];
        }

        const eligible = await checkReceiveInstantEligibility(routingNumber);
        eligibilityCache[routingNumber] = eligible;
        return eligible;
      };

      if (fedNowOptionOnly) {
        if (todayPayment && isSendPayment) {
          const eligible = await fetchEligibility();
          status = eligible ? "instant" : "standard";
        } else if (isCollectPayment) {
          // always default to standard for collect payments when only fednow option is available
          status = "standard";
        }
      } else if (sameDayOptionOnly) {
        // applies to both collect and send payments
        status = todayPayment || tomorrowPayment ? "sameDay" : "standard";
      } else if (sameDayAndFedNowOptionsOnly) {
        if (todayPayment) {
          if (isSendPayment) {
            const eligible = await fetchEligibility();
            status = eligible ? "instant" : "sameDay";
          } else if (isCollectPayment) {
            status = "sameDay";
          }
        } else if (tomorrowPayment) {
          // applies to both collect and send payments
          status = "sameDay";
        }
      }

      setSpeedStatus(status);
    };

    determineStatus();
  }, [
    hasRecipient,
    routingNumber,
    scheduledDate,
    fedNowFeature,
    sameDayFeature,
    transactionType,
  ]);

  return { speedStatus, loading };
};

const BulkPaymentRow = React.memo(
  ({
    payment,
    index,
    rowNumber,
    form,
    recipients,
    selectedRows,
    toggleRowSelection,
    handleClickEditRecipient,
    handleClickAddRecipient,
    handleClickUnsavedRecipient,
    minDate,
    setCustomAlertCopy,
    eligibilityCache,
    isTemplateMode = false,
    isExcluded = false,
    onToggleInclude,
    isFieldEditable,
    allowExpedite,
    isImportMode = false,
  }: BulkPaymentRowProps) => {
    const { l10n } = useLocalization();
    const features = useUserFeatures();
    const {
      ach_payments: achPaymentsFeature,
      expedited_payments: expeditedPaymentsFeature,
      ach_payments_collect: paymentsCollectFeature,
    } = features;
    const canSendACHPayment = Boolean(achPaymentsFeature);
    const recipientError = getRecipientError(form, index);
    const amountError = getAmountError(form, index);
    const hasDateError = getDateError(form, index);
    const transactionTypeError = getTransactionTypeError(form, index);
    const { chosenExpediteOption } = form.values;
    const { speedStatusWithExpediteOption, transactionType } = payment;
    const isCollectPayment = transactionType === "collect";
    const isSendPayment = transactionType === "send";
    const actionOptions = useMemo(() => {
      if (isImportMode) {
        return getAchImportPermittedActions(features);
      }

      const options: Array<"send" | "collect"> = [];

      if (canSendACHPayment) {
        options.push("send");
      }

      if (paymentsCollectFeature) {
        options.push("collect");
      }

      return options;
    }, [isImportMode, features, canSendACHPayment, paymentsCollectFeature]);
    const { speedStatus, loading: eligibilityLoading } = useSpeedStatus(
      features,
      payment,
      eligibilityCache
    );

    const getActionLabel = useCallback(
      (action: "send" | "collect") =>
        l10n.getString(action === "send" ? "send-label" : "collect-label"),
      [l10n]
    );

    useEffect(() => {
      if (isImportMode || !actionOptions.length) {
        return;
      }

      if (!actionOptions.includes(transactionType)) {
        form.setFieldValue(
          `payments.${index}.transactionType`,
          actionOptions[0]
        );
      }
    }, [isImportMode, actionOptions, form, index, transactionType]);

    const { onChange: onDateChange, ...restOfDateFieldProps } =
      formAdapters.fieldWithOnChange(form, `payments.${index}.scheduledDate`);

    // update speedStatusWithExpediteOption when chosenExpediteOption or speedStatus changes
    // ensures "speed" column reflects correct value based on user selection and payment eligibility
    useEffect(() => {
      if (!expeditedPaymentsFeature) return;
      if (!chosenExpediteOption || !speedStatus) return;

      const adjustedSpeed = getSpeedWithExpeditedOption(
        speedStatus,
        chosenExpediteOption
      );

      if (speedStatusWithExpediteOption !== adjustedSpeed) {
        form.setFieldValue(
          `payments.${index}.speedStatusWithExpediteOption`,
          adjustedSpeed
        );
      }
    }, [
      speedStatus,
      chosenExpediteOption,
      speedStatusWithExpediteOption,
      form,
      index,
    ]);

    // clear speedStatusWithExpediteOption if user deselects expedite option
    useEffect(() => {
      if (!chosenExpediteOption && speedStatusWithExpediteOption) {
        form.setFieldValue(
          `payments.${index}.speedStatusWithExpediteOption`,
          ""
        );
      }
    }, [chosenExpediteOption]);

    // ensures speedStatus is correctly set when recipient is added or removed
    useEffect(() => {
      if (!expeditedPaymentsFeature) return;
      const current = form.values.payments[index].speedStatus;
      if (payment.recipient?.id && current !== speedStatus) {
        form.setFieldValue(`payments.${index}.speedStatus`, speedStatus);
      }
    }, [speedStatus, index, form]);

    const handleDateChange = (value: string) => {
      const dateTime = DateTime.fromFormat(value, "M/d/yyyy");
      const formattedDate = dateTime.toFormat("MM/dd/yyyy");
      onDateChange(formattedDate);
    };

    const getSpeedCopy = useCallback(() => {
      if (
        chosenExpediteOption &&
        speedStatusWithExpediteOption &&
        isSendPayment
      ) {
        return SPEED_COPY_CONFIG[
          speedStatusWithExpediteOption as keyof typeof SPEED_COPY_CONFIG
        ].sending;
      }

      if (
        chosenExpediteOption &&
        speedStatusWithExpediteOption &&
        isCollectPayment
      ) {
        return SPEED_COPY_CONFIG[
          speedStatusWithExpediteOption as keyof typeof SPEED_COPY_CONFIG
        ].collecting;
      }

      if (!chosenExpediteOption && speedStatus in SPEED_COPY_CONFIG) {
        if (speedStatus === "standard") {
          return isSendPayment
            ? SPEED_COPY_CONFIG[speedStatus as keyof typeof SPEED_COPY_CONFIG]
                .sending
            : SPEED_COPY_CONFIG[speedStatus as keyof typeof SPEED_COPY_CONFIG]
                .collecting;
        }

        return SPEED_COPY_CONFIG[speedStatus as keyof typeof SPEED_COPY_CONFIG]
          .available;
      }

      return "";
    }, [
      chosenExpediteOption,
      speedStatus,
      speedStatusWithExpediteOption,
      transactionType,
    ]);

    const SpeedChip = useMemo(() => {
      if (eligibilityLoading) {
        return <Chip kind="info" startIcon="loader" label="" />;
      }

      if (!speedStatus) return null;

      return (
        <Chip
          kind="info"
          label={getSpeedCopy()}
          startIcon={
            speedStatusWithExpediteOption
              ? SPEED_COPY_CONFIG[
                  speedStatusWithExpediteOption as keyof typeof SPEED_COPY_CONFIG
                ].icon
              : SPEED_COPY_CONFIG[speedStatus as keyof typeof SPEED_COPY_CONFIG]
                  .icon
          }
        />
      );
    }, [eligibilityLoading, speedStatus, getSpeedCopy]);

    const customDisplayValue = useMemo(() => {
      if (!paymentsCollectFeature) {
        return undefined;
      }

      const value = filters.currency(cents(payment.amount));

      if (isCollectPayment) {
        return `+${value}`;
      }

      if (isSendPayment) {
        return `-${value}`;
      }

      return undefined;
    }, [
      paymentsCollectFeature,
      isCollectPayment,
      isSendPayment,
      payment.amount,
    ]);

    const amountInputProps = formAdapters.useAmountInput(
      form,
      `payments.${index}.amount`,
      customDisplayValue
    );

    // in template mode: determine which fields are editable
    const canEditAmount = isFieldEditable ? isFieldEditable("amount") : true;
    const canEditDescription = isFieldEditable
      ? isFieldEditable("description")
      : true;

    // in template mode when expedite not allowed, show "Sending standard"
    const templateSpeedDisplay = useMemo(() => {
      if (!isTemplateMode || !expeditedPaymentsFeature) return null;
      if (!allowExpedite) {
        return (
          <Chip
            kind="info"
            label={
              isSendPayment
                ? SPEED_COPY_CONFIG.standard.sending
                : SPEED_COPY_CONFIG.standard.collecting
            }
            startIcon={SPEED_COPY_CONFIG.standard.icon}
          />
        );
      }
      return null;
    }, [
      isTemplateMode,
      expeditedPaymentsFeature,
      allowExpedite,
      isSendPayment,
    ]);

    const getActionDropdown = useMemo(() => {
      if (isTemplateMode) {
        return (
          <span className={styles.capitalize}>
            {getActionLabel(transactionType)}
          </span>
        );
      }
      if (actionOptions.length <= 1) {
        const labelClassName = transactionTypeError
          ? `${styles.capitalize} fontColor--error`
          : styles.capitalize;
        return (
          <span className={labelClassName}>
            {getActionLabel(transactionType)}
          </span>
        );
      }
      return (
        <TableSelect
          label={l10n.getString("label-action")}
          hasError={Boolean(transactionTypeError)}
          {...formAdapters.select(form, `payments.${index}.transactionType`)}
        >
          {actionOptions.map((action) => (
            <TableSelect.Item key={action} value={action}>
              {getActionLabel(action)}
            </TableSelect.Item>
          ))}
        </TableSelect>
      );
    }, [
      isTemplateMode,
      actionOptions,
      transactionType,
      transactionTypeError,
      getActionLabel,
      l10n,
      form,
      index,
    ]);

    const dimmedStyle =
      isTemplateMode && isExcluded
        ? { opacity: 0.4, pointerEvents: "none" as const }
        : undefined;

    return (
      <Table.Row key={`table-row__${index}`}>
        {rowNumber != null && (
          <Table.Cell>
            <span className={styles.rowNumber}>{rowNumber}</span>
          </Table.Cell>
        )}
        <Table.Cell>
          {isTemplateMode ? (
            <Toggle
              isActive={!isExcluded}
              onChange={() => onToggleInclude?.()}
            />
          ) : (
            <Checkbox
              onChange={() => toggleRowSelection(index)}
              checked={selectedRows.includes(index)}
            />
          )}
        </Table.Cell>
        <Table.Cell>
          <div style={dimmedStyle}>
            <TableDateInput
              altInput
              label={l10n.getString("label-scheduled-date")}
              placeholder=""
              minDate={minDate}
              dateFormat={"m/d/Y"}
              altFormat={"m/d/Y"}
              {...{ field: "date" }}
              useIsoOnChange={false}
              onChange={handleDateChange}
              hasError={Boolean(hasDateError)}
              {...restOfDateFieldProps}
            />
          </div>
        </Table.Cell>
        <Table.Cell>
          <div style={dimmedStyle}>
            {payment.recipient?.id ? (
              <SelectedRecipientCard
                recipientId={payment.recipient?.id}
                recipient={payment.recipient}
                rowIndex={index}
                form={form}
                recipients={recipients}
                handleClickEditRecipient={
                  isTemplateMode ? undefined : handleClickEditRecipient
                }
                handleClickUnsavedRecipient={handleClickUnsavedRecipient}
                staticCard={isTemplateMode}
              />
            ) : (
              <RecipientSelector
                rowIndex={index}
                recipients={recipients}
                form={form}
                handleClickAddRecipient={handleClickAddRecipient}
                handleClickEditRecipient={handleClickEditRecipient}
                setCustomAlertCopy={setCustomAlertCopy}
                error={Boolean(recipientError)}
              />
            )}
          </div>
        </Table.Cell>
        <Table.Cell>
          <div style={dimmedStyle}>{getActionDropdown}</div>
        </Table.Cell>
        {expeditedPaymentsFeature && (
          <Table.Cell>
            <div style={dimmedStyle}>{templateSpeedDisplay || SpeedChip}</div>
          </Table.Cell>
        )}
        <Table.Cell>
          <div style={dimmedStyle}>
            {canEditDescription ? (
              <TableInput
                maxLength={MAX_MEMO_LENGTH}
                label={l10n.getString("label-enter-description")}
                placeholder={l10n.getString("label-enter-description")}
                {...formAdapters.input(form, `payments.${index}.description`)}
              />
            ) : (
              <TableInput
                label={l10n.getString("label-enter-description")}
                isDisabled={true}
                value={payment.description}
              />
            )}
          </div>
        </Table.Cell>
        <Table.Cell>
          <div style={dimmedStyle}>
            <div className={styles.rightAligned}>
              {canEditAmount ? (
                <TableInput
                  label={l10n.getString("label-amount")}
                  placeholder={l10n.getString("label-amount")}
                  {...amountInputProps}
                  hasError={Boolean(amountError)}
                />
              ) : (
                <TableInput
                  label={l10n.getString("label-amount")}
                  isDisabled={true}
                  value={
                    customDisplayValue ??
                    filters.currency(cents(payment.amount))
                  }
                />
              )}
            </div>
          </div>
        </Table.Cell>
      </Table.Row>
    );
  }
);

export default BulkPaymentRow;
